/**
 * C5 — what the builder's parts need to know about a roll-up: the agency's
 * clients, the ones this roll-up covers (saved in the document's settings)
 * and how to change them. Null inside a client's own dashboard or report.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  reportsApi,
  type Client,
  type DocumentSettings,
  type ReportDocument,
} from './reports-api.ts';

export interface RollupState {
  /** Every client of the agency, for the pickers. */
  clients: Client[];
  clientIds: string[];
  setClientIds: (ids: string[]) => void;
}

export const RollupContext = createContext<RollupState | null>(null);

export function useRollup(): RollupState | null {
  return useContext(RollupContext);
}

export function useRollupState(
  organisationId: string,
  document: Pick<ReportDocument, 'business_unit_id'>,
  settings: DocumentSettings,
  setSettings: (settings: DocumentSettings) => void,
): RollupState | null {
  const isRollup = !document.business_unit_id;
  const [clients, setClients] = useState<Client[]>([]);
  useEffect(() => {
    if (!isRollup) return;
    reportsApi
      .clients(organisationId)
      .then(setClients)
      .catch(() => setClients([]));
  }, [organisationId, isRollup]);
  return useMemo(
    () =>
      isRollup
        ? {
            clients,
            clientIds: settings.clients ?? [],
            setClientIds: (ids: string[]) => setSettings({ ...settings, clients: ids }),
          }
        : null,
    [isRollup, clients, settings, setSettings],
  );
}
